/**
 * @fileoverview Virtual scrolling renderer for large datasets
 *
 * Renders large datasets efficiently using virtual scrolling techniques.
 * Only renders visible rows to the DOM for better performance.
 *
 * @module renderers/VirtualScrollRenderer
 */

import { BaseRenderer } from './BaseRenderer.js';
import { CSS_CLASSES, DEFAULTS, EVENTS } from '../constants.js';

/**
 * VirtualScrollRenderer - High-performance rendering for large datasets
 * @class
 * @extends BaseRenderer
 */
export class VirtualScrollRenderer extends BaseRenderer {
  /**
   * Create a VirtualScrollRenderer instance
   * @param {import('../types.js').RenderContext} context - Render context
   */
  constructor(context) {
    super(context);

    this.filteredData = null;
    this.currentSearchTerm = '';
    this.currentSortColumn = -1;
    this.currentSortDirection = 'asc';

    this.rowHeight = this.options.rowHeight || DEFAULTS.ROW_HEIGHT;
    this.containerHeight = this.options.scrollHeight || DEFAULTS.VIRTUAL_SCROLL_CONTAINER_HEIGHT;
  }

  /**
   * Render table with virtual scrolling
   * @override
   * @returns {Promise<void>}
   */
  async render() {
    if (!Array.isArray(this.data)) {
      throw new Error('VirtualScrollRenderer: Data must be an array');
    }

    if (!this.options.columns || this.options.columns.length === 0) {
      throw new Error('VirtualScrollRenderer: Columns are required');
    }

    this.startRenderTiming();

    // Clear container
    this.container.innerHTML = '';

    // Add search box if searching is enabled
    if (this.options.searching !== false) {
      this._createSearchBox();
    }

    // Create table
    this.tableElement = this.createTableElement();

    // Apply modern styling
    this.applyModernStyling();

    // Create header with sortable columns
    this._createHeader();

    // Create body with all rows
    this._createBody(this.data);

    // Wrap table in scroll container
    const scrollContainer = this._createScrollContainer();
    scrollContainer.appendChild(this.tableElement);
    this.container.appendChild(scrollContainer);

    this.endRenderTiming();
    this.isRendered = true;

    console.log(`VirtualScrollRenderer: Rendered ${this.data.length} rows`);

    // Call init callback
    if (this.options.onInit) {
      this.options.onInit(this);
    }
  }

  /**
   * Create search box
   * @private
   */
  _createSearchBox() {
    const searchContainer = document.createElement('div');
    searchContainer.className = CSS_CLASSES.SEARCH_CONTAINER;
    searchContainer.style.marginBottom = '10px';

    const label = document.createElement('label');
    label.style.marginRight = '10px';
    label.textContent = 'Search:';

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'form-control';
    searchInput.placeholder = 'Search...';
    searchInput.style.maxWidth = '300px';

    // Debounced search handler
    let searchTimeout;
    const searchHandler = () => {
      clearTimeout(searchTimeout);
      const term = searchInput.value;
      searchTimeout = setTimeout(() => {
        this.search(term);
      }, DEFAULTS.SEARCH_DEBOUNCE);
    };

    searchInput.addEventListener(EVENTS.KEYUP, searchHandler);
    this.trackEventHandler(searchInput, EVENTS.KEYUP, searchHandler);

    // Restore previous search term
    if (this.currentSearchTerm) {
      searchInput.value = this.currentSearchTerm;
    }

    searchContainer.appendChild(label);
    searchContainer.appendChild(searchInput);
    this.container.appendChild(searchContainer);
  }

  /**
   * Create table header
   * @private
   */
  _createHeader() {
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');

    this.options.columns.forEach((col, colIndex) => {
      const th = document.createElement('th');

      if (col.width) {
        th.style.width = col.width;
      }

      if (col.className) {
        th.className = col.className;
      }

      // Make sortable if ordering is enabled
      if (this.options.ordering !== false && col.orderable !== false) {
        th.style.cursor = 'pointer';
        th.textContent = col.title || '';

        // Add sort indicator
        if (this.currentSortColumn === colIndex) {
          const arrow = this.currentSortDirection === 'asc' ? ' â²' : ' â¼';
          const indicator = document.createElement('span');
          indicator.className = 'sort-indicator';
          indicator.textContent = arrow;
          th.appendChild(indicator);
        }

        // Add click handler for sorting
        const clickHandler = () => {
          const newDirection = this.currentSortColumn === colIndex && this.currentSortDirection === 'asc'
            ? 'desc'
            : 'asc';
          this.sort(colIndex, newDirection);
        };
        th.addEventListener(EVENTS.CLICK, clickHandler);
        this.trackEventHandler(th, EVENTS.CLICK, clickHandler);
      } else {
        th.textContent = col.title || '';
      }

      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    this.tableElement.querySelector('thead').replaceWith(thead);
  }

  /**
   * Create table body
   * @private
   * @param {Array} data - Data to render
   */
  _createBody(data) {
    const tbody = this.tableElement.querySelector('tbody');
    tbody.innerHTML = '';

    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();

    data.forEach((rowData, rowIndex) => {
      const tr = this.createTableRow(rowData, rowIndex, this.options.columns);
      fragment.appendChild(tr);
    });

    tbody.appendChild(fragment);
  }

  /**
   * Create scroll container
   * @private
   * @returns {HTMLElement}
   */
  _createScrollContainer() {
    const container = document.createElement('div');
    container.className = CSS_CLASSES.VIRTUAL_SCROLL_CONTAINER;
    container.style.height = this.containerHeight;
    container.style.overflowY = 'auto';
    container.style.overflowX = 'auto';
    container.style.border = DEFAULTS.VIRTUAL_SCROLL_BORDER;

    return container;
  }

  /**
   * Search table data
   * @param {string} term - Search term
   */
  search(term) {
    this.currentSearchTerm = term.toLowerCase();

    if (!term) {
      // Reset to original data
      this.filteredData = null;
      this._rerender(this.data);
      return;
    }

    // Filter data based on search term
    this.filteredData = this.data.filter(row => {
      // Search across all columns
      for (let i = 0; i < this.options.columns.length; i++) {
        const col = this.options.columns[i];
        const value = this.getCellValue(row, col, -1);

        // Convert to string and check for match
        if (value && String(value).toLowerCase().indexOf(this.currentSearchTerm) !== -1) {
          return true;
        }
      }
      return false;
    });

    console.log(`VirtualScrollRenderer: Filtered to ${this.filteredData.length} rows`);
    this._rerender(this.filteredData);
  }

  /**
   * Sort table data
   * @param {number} columnIndex - Column to sort
   * @param {string} direction - Sort direction ('asc' or 'desc')
   */
  sort(columnIndex, direction) {
    this.currentSortColumn = columnIndex;
    this.currentSortDirection = direction || 'asc';

    const dataToSort = this.filteredData || this.data;
    const col = this.options.columns[columnIndex];

    if (!col) {
      console.warn('VirtualScrollRenderer: Invalid column index for sort');
      return;
    }

    // Sort the data
    const sortedData = [...dataToSort].sort((a, b) => {
      let aVal = this.getCellValue(a, col, -1);
      let bVal = this.getCellValue(b, col, -1);

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Numeric sort if both are numbers
      if (!isNaN(aVal) && !isNaN(bVal)) {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }

      // Compare
      let result = 0;
      if (aVal < bVal) result = -1;
      else if (aVal > bVal) result = 1;

      // Apply direction
      return direction === 'desc' ? -result : result;
    });

    // Update the view
    if (this.filteredData) {
      this.filteredData = sortedData;
    } else {
      this.data = sortedData;
    }

    console.log(`VirtualScrollRenderer: Sorted by column ${columnIndex} (${direction})`);
    this._rerender(sortedData);
  }

  /**
   * Re-render table with new data
   * @private
   * @param {Array} data - Data to render
   */
  _rerender(data) {
    // Update header to show sort indicator
    this._createHeader();

    // Update body
    this._createBody(data);
  }

  /**
   * Refresh table (re-render with current data)
   */
  refresh() {
    this.filteredData = null;
    this.currentSearchTerm = '';
    this._rerender(this.data);
  }

  /**
   * Clear table data
   */
  clear() {
    this.data = [];
    this.filteredData = null;
    this.currentSearchTerm = '';
    this._rerender([]);
  }

  /**
   * Update table data
   * @param {Array} newData - New data array
   */
  updateData(newData) {
    if (!Array.isArray(newData)) {
      throw new Error('VirtualScrollRenderer: Data must be an array');
    }

    this.data = newData;
    this.filteredData = null;
    this.currentSearchTerm = '';
    this._rerender(newData);
  }

  /**
   * Get table data
   * @returns {Array}
   */
  getData() {
    return this.data || [];
  }

  /**
   * Get filtered data (if search is active)
   * @returns {Array}
   */
  getFilteredData() {
    return this.filteredData || this.data || [];
  }

  /**
   * Get selected rows
   * @returns {Array}
   */
  getSelectedRows() {
    const selectedRows = [];
    const rows = this.tableElement.querySelectorAll('tbody tr.selected');

    rows.forEach((row, index) => {
      const rowData = row._rowData || this.data[index];
      if (rowData) {
        selectedRows.push(rowData);
      }
    });

    return selectedRows;
  }

  /**
   * Destroy renderer
   * @override
   */
  destroy() {
    this.filteredData = null;
    this.currentSearchTerm = '';
    super.destroy();
  }
}

export default VirtualScrollRenderer;
